import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * Era Vanta: the top level is a modal <dialog> (opened with showModal(), like the
 * cart drawer) instead of Horizon's <details>, so it renders in the browser's top
 * layer and can't be offset by transforms or containment on header ancestors.
 * Submenus inside it (menus with 3+ levels) are still nested <details> and keep
 * Horizon's open/back behaviour.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The hamburger button that opens the drawer.
 * @property {HTMLDialogElement} dialog - The modal dialog shell.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['trigger', 'dialog', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('cancel', this.#onCancel);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('cancel', this.#onCancel);
    document.documentElement.removeAttribute('scroll-lock');
  }

  /**
   * Escape fires `cancel` on the modal dialog. Replace the instant native close
   * with the animated one, closing the innermost open submenu first.
   * @param {Event} event
   */
  #onCancel = (event) => {
    event.preventDefault();

    const openSubmenus = this.refs.dialog.querySelectorAll('details.menu-open');
    const innermost = openSubmenus[openSubmenus.length - 1];

    if (innermost instanceof HTMLDetailsElement) {
      this.#closeSubmenu(innermost);
    } else {
      this.close();
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.dialog.open;
  }

  /**
   * Get the submenu <details> the event came from, if any
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement | null}
   */
  #getSubmenu(event) {
    if (!(event?.target instanceof Element)) return null;

    const details = event.target.closest('details');
    return details && this.refs.dialog.contains(details) ? details : null;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open a submenu (when called from inside one) or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const submenu = this.#getSubmenu(event);

    if (submenu) {
      this.#openSubmenu(submenu, target);
      return;
    }

    const { dialog, trigger } = this.refs;
    if (dialog.open) return;

    this.preventInitialAccordionAnimations(dialog);
    document.documentElement.setAttribute('scroll-lock', '');
    dialog.showModal();
    trigger.setAttribute('aria-expanded', 'true');

    // Add the class on the next frame so the panel transitions in from its closed state
    requestAnimationFrame(() => dialog.classList.add('menu-open'));
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {string} [target]
   */
  #openSubmenu(details, target) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the submenu animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back from a submenu, or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    const submenu = this.#getSubmenu(event);

    if (submenu) {
      this.#closeSubmenu(submenu);
    } else {
      this.close();
    }
  }

  /**
   * Close the main menu drawer (and any submenus open inside it)
   */
  close() {
    const { dialog, trigger, menuDrawer } = this.refs;
    if (!dialog.open || !dialog.classList.contains('menu-open')) return;

    trigger.setAttribute('aria-expanded', 'false');
    dialog.classList.remove('menu-open');
    menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the panel's own transition, not the whole subtree (see Horizon's
    // note: child accordion animations can stall this on Firefox)
    onAnimationEnd(
      menuDrawer,
      () => {
        removeTrapFocus();
        dialog.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
        dialog.close();
        document.documentElement.removeAttribute('scroll-lock');
        trigger.focus({ preventScroll: true });
      },
      { subtree: false }
    );
  }

  /**
   * Close one open submenu and return focus handling to the dialog
   *
   * @param {HTMLDetailsElement} details
   */
  #closeSubmenu(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const drawer = details.querySelector('.menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        // The modal dialog already keeps focus inside it; drop the submenu trap
        removeTrapFocus();
        summary.focus({ preventScroll: true });
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLElement} details - The element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
